import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import ModalBase from "../../../../components/ui/ModalBase";
import colors from "../../../../shared/constants/color";
import axios from "axios";
import { SERVICEURL } from "../../../../appconfig";
import { useToastContext } from "../../../../context/ToastContextProvider";
import {
  getEmailDetail,
  updateEmailInfo,
} from "../../../../api/emailApi";
import { isCorporateCommunicationParameterId } from "../../../../constants/communicationParameterGuids";

interface AddEmailModalProps {
  open: boolean;
  memberId: string | number;
  emailId?: string | null;
  onClose: () => void;
}

export interface EmailInfoListDTO {
  EmailID: number;
  MemberID: number;
  CommunicationType: string;
  EmailName: string;
}

const initialFormState = {
  emailType: "",
  email: "",
};

const AddEmailModal: React.FC<
  AddEmailModalProps & { onSuccess?: (item: EmailInfoListDTO) => void }
> = ({
  open,
  memberId,
  emailId = null,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!emailId;
  const [form, setForm] = useState<typeof initialFormState>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailTypeOptions, setEmailTypeOptions] = useState<any[]>([]);
  const { showToast } = useToastContext();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const resetState = () => {
    setForm(initialFormState);
    setErrors({});
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined;

        // Email tiplerini yükle
        const response = await axios.get(
          `${SERVICEURL}api/Data/GetCommunicationParameterList`,
          { headers }
        );

        const emailTypeData = response.data?.data || [];

        // Dalga 22 notu: adres tipi listesi CommunicationParameter.IsAdress'e baglandi,
        // e-posta tipi listesi BILEREK ad bazli kaldi. Upstream 1070446/ba2026f de mail
        // yolunu degistirmedi ve uretimde IsMail = {IS(2), OZEL(3), Kurumsal(13)} -- bu
        // filtrenin sonucuyla ayni. Bayraga gecmek ayri bir dalganin isi (uretimdeki
        // IsTel/IsMail tutarsizliklari icin bkz. 011_CommunicationParameterContactFlags.sql).
        const filteredEmailTypes = Array.isArray(emailTypeData)
          ? emailTypeData.filter(
              (type: any) =>
                type.name === "İŞ" ||
                type.name === "ÖZEL" ||
                isCorporateCommunicationParameterId(type.id)
            )
          : [];

        setEmailTypeOptions(filteredEmailTypes);
        console.log("Email type options loaded:", filteredEmailTypes);

        // Düzenleme modundaysa email verisini yükle
        if (emailId) {
          const data = await getEmailDetail(emailId);
          console.log("Loaded email data:", data);

          // Email type ID'sini dropdown options'larında ara
          const matchingType = filteredEmailTypes.find(
            (type: any) => type.id === data.communicationParameterId
          );
          
          console.log("Matching type found:", matchingType);

          setForm({
            emailType: data.communicationParameterId?.toString() || "",
            email: data.emailName || "",
          });

          console.log("Form set with:", {
            communicationParameterId: data.communicationParameterId,
            emailName: data.emailName,
            formEmailType: data.communicationParameterId?.toString(),
          });
        } else {
          resetState();
        }
      } catch (error) {
        console.error("Data loading error:", error);
        if (emailId) {
          showToast("Email bilgisi yüklenirken bir hata oluştu.", "error");
        } else {
          console.error("E-Mail tipleri yüklenirken hata oluştu:", error);
        }
      }
    };

    fetchData();
  }, [open, emailId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!form.emailType) {
      newErrors.emailType = "E-Mail tipi seçimi zorunludur.";
    }
    if (!form.email) {
      newErrors.email = "E-Mail adresi girilmesi zorunludur.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Geçerli bir e-mail adresi giriniz.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);

    try {
      setIsSubmitting(true);

      if (isEditMode && emailId) {
        // Düzenleme modu - PUT isteği
        const updatePayload = {
          emailId: emailId,
          emailName: form.email,
          communicationParameterId: form.emailType || undefined,
        };

        console.log("Update email payload:", updatePayload);
        await updateEmailInfo(updatePayload);
        showToast("Email bilgisi başarıyla güncellendi.", "success");
        if (onSuccess) {
          onSuccess({} as EmailInfoListDTO);
        }
        handleClose();
      } else {
        // Ekleme modu - POST isteği
        const token = localStorage.getItem("token");
        const currentUserId = localStorage.getItem("memberId");

        const selectedEmailType = emailTypeOptions.find(
          (type: any) => type.id === Number(form.emailType)
        );

        const payload = {
          EmailName: form.email,
          CommunicationParameterID: form.emailType || null,
          EnteringMemberID: null,
        };

      const response = await axios.post(
        `${SERVICEURL}api/Candidates/AddEmailInfo/${memberId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      
      const responseData = response.data?.data || response.data;
      
      const apiData: EmailInfoListDTO = {
        EmailID: responseData?.emailID || responseData?.EmailID || 0,
        MemberID: responseData?.memberID || responseData?.MemberID || 0,
        CommunicationType: responseData?.communicationType || responseData?.CommunicationType || "",
        EmailName: responseData?.emailName || responseData?.EmailName || "",
      };

      if (apiData.CommunicationType && apiData.EmailName && onSuccess) {
        onSuccess(apiData);
      } else {
        console.warn("API response is invalid or missing required fields:", apiData, "Original:", responseData);
      }

        showToast("E-Mail bilgisi başarıyla eklendi.", "success");
        handleClose();
      }
    } catch (error: any) {
      console.error("Email save error:", error);
      const errorMessage = isEditMode
        ? error.response?.data?.message || "Email bilgisi güncellenirken bir hata oluştu."
        : error.response?.data?.message || "E-Mail eklenirken bir hata oluştu.";
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <ModalBase
      open={open}
      onClose={handleClose}
      title={isEditMode ? "E-Mail Düzenle" : "Yeni E-Mail Ekle"}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ borderRadius: 2, textTransform: "none", minWidth: 80 }}
          >
            Kapat
          </Button>
          <Button
            variant="contained"
            sx={{
              borderRadius: 2,
              minWidth: 80,
            }}
            disabled={isSubmitting}
            onClick={handleSaveClick}
          >
            {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </>
      }
      maxWidth="md"
      minWidth={700}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          width: "100%",
        }}
      >
        <TextField
          select
          fullWidth
          label="E-Mail Tipi*"
          name="emailType"
          value={form.emailType}
          onChange={handleChange}
          size="small"
          error={!!errors.emailType}
          helperText={errors.emailType}
        >
          <MenuItem value="">Seçiniz</MenuItem>
          {emailTypeOptions.map((type) => (
            <MenuItem key={type.id} value={type.id.toString()}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="E-Mail*"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          size="small"
          error={!!errors.email}
          helperText={errors.email}
          placeholder="ornek@email.com"
        />
      </Box>
    </ModalBase>
    <Dialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      disableScrollLock
    >
      <DialogTitle>
        {isEditMode ? "E-posta Güncellensin mi?" : "E-posta Eklensin mi?"}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {isEditMode 
            ? "E-posta güncellemek istediğinize emin misiniz?" 
            : "E-posta eklemek istediğinize emin misiniz?"}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
          Hayır
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          sx={{ textTransform: "none", background: colors.blue.B800 }}
        >
          Evet
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddEmailModal;

